import * as React from 'react';
import { z } from 'zod';
import { Button } from './Button';
import { Dialog, DialogContent } from './Dialog';
import {
  DriverTruckStatusFields,
  driverTruckStatusShape,
  type DriverTruckOption,
} from './DriverTruckStatusFields';
import { FormActions } from './Field';
import { Form, FormField, type ZodForm } from './Form';
import { Input } from './Input';
import {
  PHONE_VALIDATION_MESSAGE,
  PhoneInput,
  isValidPhoneNumber,
} from './PhoneInput';

export const editDriverSchema = z.object({
  name: z.string().min(1, 'Name is required'),
  /** Optional, but once given it has to be a well-formed number. */
  employeeNumber: z
    .string()
    .optional()
    .refine(
      (v) => !v || /^[A-Za-z0-9\-_]+$/.test(v),
      'Employee number can only contain letters, numbers, hyphens, and underscores'
    )
    .refine(
      (v) => !v || v.length <= 50,
      'Employee number must not exceed 50 characters'
    ),
  phone: z
    .string()
    .optional()
    .refine((v) => !v || isValidPhoneNumber(v), PHONE_VALIDATION_MESSAGE),
  email: z.string().min(1, 'Email is required').email('Invalid email format'),
  ...driverTruckStatusShape,
});

export type EditDriverValues = z.infer<typeof editDriverSchema>;
export type EditDriverForm = ZodForm<typeof editDriverSchema>;

export interface EditDriverDialogViewProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** From `useZodForm(editDriverSchema, …)`. */
  form: EditDriverForm;
  trucks: DriverTruckOption[];
  trucksLoading?: boolean;
  /** Something else is in flight: submit locks. */
  canSubmit?: boolean;
  /** The update is saving: the submit button relabels and locks. */
  submitting?: boolean;
  onSubmit: (values: EditDriverValues) => void;
}

/**
 * Edit a driver: name, employee number, phone and email, then the truck
 * and status. What they may do is their office role's and the Driver set's,
 * set on the Team and Roles pages, not here.
 *
 *   const form = useZodForm(editDriverSchema, { defaultValues: driver });
 *   <EditDriverDialogView open={open} onOpenChange={setOpen} form={form}
 *     trucks={trucks} submitting={isPending} onSubmit={save} />
 *
 * Enter does not submit — the selects are part of the dialog, and a stray
 * Enter in one of them should not save half an edit.
 */
export function EditDriverDialogView({
  open,
  onOpenChange,
  form,
  trucks,
  trucksLoading = false,
  canSubmit = true,
  submitting = false,
  onSubmit,
}: EditDriverDialogViewProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title="Edit Driver">
        <Form
          form={form}
          onSubmit={onSubmit}
          onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter') event.preventDefault();
          }}
        >
          <FormField form={form} name="name" label="Name">
            {(field) => <Input {...field} placeholder="Enter full name" />}
          </FormField>

          <FormField
            form={form}
            name="employeeNumber"
            label="Employee Number"
            description="Unique identifier for this driver within the company"
          >
            {(field) => <Input {...field} placeholder="e.g., DRV-001" />}
          </FormField>

          <FormField form={form} name="phone" label="Contact Phone">
            {(field) => <PhoneInput {...field} placeholder="(xxx) xxx-xxxx" />}
          </FormField>

          <FormField form={form} name="email" label="Email">
            {(field) => (
              <Input type="email" {...field} placeholder="Enter email" />
            )}
          </FormField>

          <DriverTruckStatusFields
            form={form}
            truckName="truckId"
            statusName="status"
            trucks={trucks}
            trucksLoading={trucksLoading}
          />

          <FormActions>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancel
            </Button>
            <Button type="submit" disabled={!canSubmit || submitting}>
              {submitting ? 'Updating...' : 'Update Driver'}
            </Button>
          </FormActions>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
